import { apiRequest } from './axiosConfig';
import axios from 'axios';
import { handleApiError } from '../../utils/errorHandling';

export const CREDIT_REFRESH_EVENT = 'tasky:credits:refresh';

// Credit wallet balance info
export interface CreditBalance {
  organization_name: string;
  wallets: {
    monthly: {
      balance: number;
      limit: number;
      expires_at: string | null;
    };
    free: {
      balance: number;
      daily_amount: number;
      expires_at: string | null;
    };
    additional: {
      balance: number;
    };
  };
  total_balance: number;
  /**
   * これまでに招待で得たクレジットの累計。
   *
   * 招待ボーナスは additional ウォレットに購入分と混ざって入るため、
   * 「残高のうち何crがボーナス由来か」は出せない。ここにあるのは累計付与額。
   */
  invite_bonus_total: number;
}

// Credit transaction record
export interface CreditTransaction {
  id: number;
  organization_id: number;
  user_id: number | null;
  wallet_type: 'monthly' | 'free' | 'additional';
  transaction_type: 'grant' | 'consume' | 'reset' | 'expire';
  amount: number;
  balance_after: number;
  description: string | null;
  ai_usage_log_id: number | null;
  created_at: string;
  user?: { id: number; name: string } | null;
}

// Paginated transaction response
export interface PaginatedTransactions {
  data: CreditTransaction[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/**
 * Fetch credit balance for the current user's organization.
 */
export const fetchUserCreditBalance = async (): Promise<CreditBalance> => {
  try {
    const response = await apiRequest.get('users/credits/balance');
    const responseData = response as unknown as { data: CreditBalance; success: boolean };
    return responseData.data;
  } catch (error) {
    // CreditBadge のポーリングが 401/403 で止まれるよう、HTTP ステータスを残す。
    throw Object.assign(handleApiError(error, 'クレジット残高の取得に失敗しました'), {
      status: axios.isAxiosError(error) ? error.response?.status : undefined,
    });
  }
};

/**
 * Fetch credit transaction history for the current user's organization.
 */
export const fetchUserCreditTransactions = async (
  params?: { per_page?: number; page?: number; wallet_type?: string; transaction_type?: string }
): Promise<PaginatedTransactions> => {
  try {
    const response = await apiRequest.get('users/credits/transactions', { params });
    const responseData = response as unknown as { data: PaginatedTransactions; success: boolean };
    return responseData.data;
  } catch (error) {
    throw handleApiError(error, 'クレジット取引履歴の取得に失敗しました');
  }
};
